import { useState, useEffect, useCallback } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  IconButton,
  Modal,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { ProductDraft } from '../../context/ProductContext';
import ProductCard from './ProductCard';
import theme from '../../theme';
import { useProductData } from '../../hooks/useProductData';
import { ContentCopy, Link } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import CONFIG from '../../config/config';

export default function ProductList() {
  const {
    allProducts,
    productInfo,
    handleDeleteProduct,
    toggleStatus,
    incrementClickCount,
  } = useProductData();
  const { authUser } = useAuth();
  const navigate = useNavigate();
  const [hoveredProduct, setHoveredProduct] = useState<any | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [enterTimer, setEnterTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const OPEN_DELAY = 1000; // ms

  const openModalNow = (product: any) => {
    if (enterTimer) {
      clearTimeout(enterTimer);
      setEnterTimer(null);
    }
    setHoveredProduct(product);
    setShowPreviewModal(true);
  };

  const handleRowEnter = (_e: React.MouseEvent<HTMLElement>, product: any) => {
    if (isMobile) return; // no hover on mobile
    if (enterTimer) {
      clearTimeout(enterTimer);
    }
    setHoveredProduct(product);
    const t = setTimeout(() => {
      setShowPreviewModal(true);
      setEnterTimer(null);
    }, OPEN_DELAY);
    setEnterTimer(t);
  };

  const handleRowLeave = () => {
    if (enterTimer) {
      clearTimeout(enterTimer);
      setEnterTimer(null);
    }
  };

  const handleCloseModal = () => {
    if (enterTimer) {
      clearTimeout(enterTimer);
      setEnterTimer(null);
    }
    setShowPreviewModal(false);
    setHoveredProduct(null);
  };

  const handleEditProduct = (id: string) => {
    navigate(`edit?product=${id}`);
  };

  const deleteProductHandler = (product: ProductDraft) => {
    handleDeleteProduct(authUser?.uid, product.id);
    setShowDeleteModal(false);
  };

  const handleCopyToClipboard = async (
    shortCode: string | null,
    productId: string | null
  ) => {
    const link = `${CONFIG.MAIN_URL}${authUser?.userName}?${
      shortCode ? `s_id=${shortCode}` : `p_id=${productId}`
    }`;
    navigator?.clipboard?.writeText?.(link);
    toast.success('Link copied!');
    setShowShareModal(false);

    // Log the click via centralized ProductContext (which handles Shopify logging)
    if (productId && authUser?.uid) {
      incrementClickCount(authUser.uid, productId);
    }
  };
  const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    margin: 'auto',
    maxWidth: { xs: '90vw', md: '600px' },
    minWidth: { xs: '60vw', md: '500px' },
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '10px',
    p: 3,
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header (hidden on mobile) */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'grid' },
          gridTemplateColumns: '50px 1.5fr 60px 60px 60px 60px',
          alignItems: 'center',
          columnGap: 2,
          px: 1,
          pb: 0.5,
          color: (t) => t.palette.text.secondary,
          fontSize: 13,
        }}
      >
        <Typography variant='caption' sx={{ fontWeight: 600 }} />
        <Typography variant='caption' sx={{ fontWeight: 600 }}>
          Product
        </Typography>
        <Typography
          variant='caption'
          sx={{ fontWeight: 600, textAlign: 'center' }}
        ></Typography>
        <Typography
          variant='caption'
          sx={{ fontWeight: 600, textAlign: 'center' }}
        >
          Status
        </Typography>
        <Typography
          variant='caption'
          sx={{ fontWeight: 600, textAlign: 'center' }}
        >
          Mentions
        </Typography>
        <Typography
          variant='caption'
          sx={{ fontWeight: 600, textAlign: 'center' }}
        >
          Clicks
        </Typography>
        <Box />
      </Box>

      {/* Rows */}
      <Box
        sx={{ mt: 0.5, borderTop: 1, borderColor: (t) => t.palette.divider }}
      >
        {allProducts &&
          allProducts.map((product, idx) => (
            <Box
              key={`${product.name}-${idx}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '60px 1fr', // mobile
                  sm: '50px 1.5fr 60px 60px 60px 60px', // desktop/tablet (unchanged)
                },
                gridTemplateAreas: {
                  xs: `
          "thumb title"
          "meta  actions2"
        `,
                  sm: 'unset',
                },
                alignItems: 'center',
                columnGap: 2,
                rowGap: { xs: 0.5, sm: 0 },
                px: 1,
                py: 1,
                borderBottom: 1,
                borderColor: (t) => t.palette.divider,
                cursor: 'pointer',
                opacity: product.isActive ? 1 : 0.5,
              }}
            >
              {/* Thumb */}
              <Box
                sx={{ gridArea: { xs: 'thumb', sm: 'auto' }, display: 'flex' }}
                onClick={() => openModalNow(product)} // tap opens preview on mobile
                onMouseEnter={(e) => handleRowEnter(e, product)}
                onMouseLeave={handleRowLeave}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    overflow: 'hidden',
                    borderRadius: 1.5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={product.imageURL ?? ''}
                    alt={product.name ?? ''}
                    style={{
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                  />
                </Box>
              </Box>

              {/* Title — WRAPS */}
              <Box
                sx={{ gridArea: { xs: 'title', sm: 'auto' }, minWidth: 0 }}
                onClick={() => openModalNow(product)} // tap opens preview on mobile
                onMouseEnter={(e) => handleRowEnter(e, product)}
                onMouseLeave={handleRowLeave}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 500,
                    whiteSpace: 'normal', // allow wrapping
                    wordBreak: 'break-word',
                    overflow: 'visible',
                    maxWidth: { xs: 'unset', sm: '800px' },
                    lineHeight: 1.3,
                  }}
                  title={product.name}
                >
                  {product.name}
                </Typography>
              </Box>

              {/* Desktop cells (unchanged) */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  justifyContent: 'center',
                }}
              >
                <Tooltip title='Share'>
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={!product?.isActive}
                    startIcon={<Link fontSize='small' />}
                    sx={{ textTransform: 'none', borderRadius: 2, px: 1 }}
                    onClick={() =>
                      handleCopyToClipboard(product?.shortCode, product?.id)
                    }
                  >
                    Copy
                  </Button>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  justifyContent: 'center',
                }}
              >
                <Switch
                  size='small'
                  checked={product.isActive}
                  onChange={() => toggleStatus(authUser?.uid, product?.id)}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  justifyContent: 'center',
                }}
              >
                <Typography variant='body2'>{product.viewCount}</Typography>
              </Box>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  justifyContent: 'center',
                }}
              >
                <Typography variant='body2'>
                  {product.clickCount ?? 0}
                </Typography>
              </Box>

              {/* Mobile: Mentions / Clicks (2nd line, left) */}
              <Box
                sx={{
                  gridArea: { xs: 'meta', sm: 'auto' },
                  display: { xs: 'flex', sm: 'none' },
                  gap: 2,
                  color: 'text.secondary',
                  fontSize: 13,
                }}
              >
                <Typography variant='caption'>
                  Mentions: {product.viewCount ?? 0}
                </Typography>
                <Typography variant='caption'>
                  Clicks: {product.clickCount ?? 0}
                </Typography>
              </Box>

              {/* Mobile: Actions row 1 (Copy + Edit) — right of top line */}
              <Box
                sx={{
                  gridArea: { xs: 'actions2', sm: 'auto' },
                  display: { xs: 'flex', sm: 'none' },
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Switch
                  size='small'
                  checked={product.isActive}
                  onChange={() => toggleStatus(authUser?.uid, product?.id)}
                />
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<Link fontSize='small' />}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                  onClick={() => {
                    handleCopyToClipboard(product?.shortCode, product?.id);
                  }}
                >
                  Copy
                </Button>
              </Box>

              {/* Mobile: Actions row 2 (Switch) — right of second line */}
            </Box>
          ))}
      </Box>

      {/* preview modal */}
      <Modal
        open={showPreviewModal}
        onClose={handleCloseModal}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box sx={modalStyle}>
          {hoveredProduct && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant='h6'>Product Listing Preview</Typography>
                <Typography variant='body2'>
                  This is how your product will appear in chats and replies*
                </Typography>
              </Box>

              <Box sx={{ height: { xs: 300, md: 150 } }}>
                <ProductCard product={hoveredProduct} wrap={isMobile} />
              </Box>

              <Typography variant='body2'>
                *Appearance may vary depending on viewer&#39;s device display
              </Typography>

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                }}
              >
                <Button
                  variant='contained'
                  sx={{
                    border: `${theme.palette.primary._states.outlinedBorder}!important`,
                    bgcolor: `${theme.palette.secondary.main} !important`,
                    color: `${theme.palette.secondary.contrastText} !important`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                  onClick={() => handleEditProduct(hoveredProduct.id)}
                >
                  <Typography variant='body2'>Edit</Typography>
                  <EditIcon fontSize='small' />
                </Button>

                <Button
                  variant='contained'
                  sx={{
                    border: `${theme.palette.primary._states.outlinedBorder}`,
                    bgcolor: `${theme.palette.primary.main}`,
                    color: `${theme.palette.primary.contrastText}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                  onClick={() => {
                    navigate(
                      `/${authUser?.userName}/product/chat?p_id=${hoveredProduct.id}`
                    );
                  }}
                >
                  <Typography variant='body2'>Chat</Typography>
                </Button>

                <Button
                  variant='contained'
                  sx={{
                    border: `${theme.palette.primary._states.outlinedBorder}`,
                    bgcolor: `${theme.palette.secondary.main}`,
                    color: `${theme.palette.secondary.contrastText}`,
                  }}
                  onClick={handleCloseModal}
                >
                  <Typography variant='body2'>Done</Typography>
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Delete confirm modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant='h6'>Delete this product?</Typography>
            <ProductCard product={productInfo} wrap={isMobile} />
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                variant='contained'
                onClick={() => setShowDeleteModal(false)}
                sx={{
                  border: `${theme.palette.primary._states.outlinedBorder}`,
                  bgcolor: `${theme.palette.secondary.main}`,
                  color: `${theme.palette.secondary.contrastText}`,
                }}
              >
                <Typography variant='body2'>Cancel</Typography>
              </Button>
              <Button
                variant='contained'
                onClick={() => deleteProductHandler(productInfo)}
                sx={{
                  border: `${theme.palette.primary._states.outlinedBorder}`,
                  bgcolor: `${theme.palette.error.main} !important`,
                  color: `${theme.palette.error.contrastText} !important`,
                }}
              >
                <Typography variant='body2'>Delete</Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Share / Magic Link modal */}
      {/* <Modal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ProductCard product={productInfo} />

            <Box>
              <Typography variant="body2">Copy Magic Link to Share Product</Typography>

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                }}
              >
                <TextField
                  fullWidth
                  value={`${CONFIG.MAIN_URL}/${authUser?.userName}?${productInfo?.shortCode ? `s_id=${productInfo?.shortCode}` : `p_id=${productInfo?.id}`}`}
                  disabled
                  slotProps={{
                    input: {
                      sx: {
                        bgcolor: theme.palette.action.disabledBackground,
                        color: 'text.primary',
                        height: '100%',
                        borderRadius: 2,
                        '& fieldset': {
                          border: `1px solid ${theme.palette['_components'].input.standard.enabledBorder}`,
                        },
                      },
                    },
                  }}
                  variant="outlined"
                  sx={{
                    height: 40,
                    flex: 1,
                    '& .MuiInputBase-root': { height: '100%' },
                  }}
                />

                <IconButton
                  aria-label="copy-to-clipboard"
                  size="small"
                  onClick={() => handleCopyToClipboard(productInfo?.shortCode, productInfo?.id)}
                >
                  <ContentCopy />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal> */}
    </Box>
  );
}
